import React, { useEffect } from "react";
import styled from "styled-components";
import S from "sanctuary";
import { connect } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { FaUser, FaBars } from "react-icons/fa";
import {
  Collapse,
  NavbarToggler,
  NavbarBrand,
  NavLink,
  NavItem,
  Navbar,
  Nav,
} from "reactstrap";
import { useOktaAuth } from "@okta/okta-react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  ButtonWithLink,
  Alert,
  Center,
  Cluster,
  Text,
  constants,
} from "@thecb/components";

import { toggleUserMenu, toggleCommonMenu, closeMenus } from "@/actions/header";
import {
  currentUserIsCitybaseAdmin,
  getCurrentUserClient,
  removeClient,
} from "@/components/currentUser/CurrentUser.state";
import { clearState } from "@/components/transactionSearchResults/TransactionSearchResults.state";
import { clearState as clearStateV2Beta } from "@/components/transactionSearchResultsV2Beta/TransactionSearchResultsV2Beta.state";
import { resetState as resetStateV2Beta } from "@/components/transactionSearchPageV2Beta/TransactionSearchPageV2Beta.state";
import HeaderLogo from "@/components/headerLogo";
import { getCurrentUserEmail } from "@/components/currentUser/CurrentUser.state";
import { USER_PROFILE } from "@/constants/routes";
import { AI_PRIMARY_HOVER, BACKGROUND_DARK, BETA_ORANGE } from "@/constants/colors";
import { NAV_WIDE_SPACING_MIN_WIDTH } from "@/constants/layout";
import {
  clearClientSettings,
  getCustomerManagementEnabled,
} from "@/state/client.state";
import { logOut } from "@/util/session";
import { clearPeriscopeState } from "../periscopeDashboard/PeriscopeDashboard.state";

// Sits inside the tab's text span, so the tab's hover recolors only the title.
const TabBadge = styled.span`
  display: inline-block;
  vertical-align: middle;
  line-height: 1;
  margin-left: 0.125rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: ${BETA_ORANGE};
  color: ${AI_PRIMARY_HOVER};
  border-radius: 999px;
  padding: 0.25rem 0.5rem;
`;

const Tab = ({
  slug,
  title,
  containsSubroutes = false,
  dataQa = title,
  badge,
  compact = false,
}) => {
  const { pathname } = useLocation();
  const firstPathSegment = (path) => path.split("/")[1];
  const isActive = containsSubroutes
    ? pathname.includes(firstPathSegment(slug))
    : pathname === slug;
  const textExtraStyles = `
      font-size: 1rem;
      font-weight: 600;
      text-decoration: none;
      color: ${isActive ? constants.colors.LUCKY_POINT : BACKGROUND_DARK};
      transition: color .5s ease;
    `;
  return (
    <ButtonWithLink
      variant="ghost"
      url={slug}
      text={title}
      dataQa={dataQa}
      linkExtraStyles={`
        text-decoration: none;
      `}
      textExtraStyles={textExtraStyles}
      // `ButtonWithAction` renders children only with `contentOverride`, so a
      // badged tab rebuilds the default Center > Text markup around its pill.
      contentOverride={!!badge}
      extraStyles={`
      margin: 0 1rem;
      padding: 0.65rem 0 0 0;

      @media (min-width: ${NAV_WIDE_SPACING_MIN_WIDTH}) {
        margin: 0 ${compact ? "1.25rem" : "1.9rem"};
      }

      &:after {
        content: "";
        display: block;
        width: ${isActive ? "100%" : "0"};
        padding-top: 0.65rem;
        border-bottom: 4px solid ${constants.colors.LUCKY_POINT};
        transition: width .5s ease;
      }

      &:hover, &:focus {
        text-decoration: none;
        > * > span {
          color: ${constants.colors.LUCKY_POINT};
          text-decoration: none;
        }

        &:after {
          width: 100%;
        }
      }
    `}
    >
      {badge && (
        <Center as="span" intrinsic>
          <Text extraStyles={textExtraStyles}>
            {title} <TabBadge>{badge}</TabBadge>
          </Text>
        </Center>
      )}
    </ButtonWithLink>
  );
};

export const Header = ({
  toggleUserMenu,
  toggleCommonMenu,
  closeMenus,
  header: { userMenuCollapsed, commonMenuCollapsed },
  userEmail,
  isCustomerManagementEnabled,
  hideNavbar = false,
  isCitybaseAdmin,
  client,
  removeClient,
  clearClientSettings,
  clearState,
  clearStateV2Beta,
  resetStateV2Beta,
  clearPeriscopeState,
}) => {
  const { oktaAuth } = useOktaAuth();
  const flags = useFlags();
  const paymentsBetaTabEnabled = flags?.["revm-payments-beta-tab"];
  const { pathname } = useLocation();

  useEffect(() => {
    closeMenus();
  }, [pathname, closeMenus]);

  const cbAdminText = (
    <Cluster justify="center" align="center">
      <Text variant="pS">
        You are viewing{" "}
        <span style={{ fontWeight: "700" }}>{client?.value?.brand ?? ""}</span>.
      </Text>
      <ButtonWithLink
        url="/admin"
        text="Change client"
        variant="smallGhost"
        linkExtraStyles={`
          text-decoration: none;

          &:hover, &:focus {
            text-decoration: underline;
          }
        `}
        textExtraStyles={`font-size: 0.875rem;`}
        action={() => {
          removeClient();
          clearClientSettings();
          clearState();
          clearStateV2Beta();
          resetStateV2Beta();
          clearPeriscopeState();
        }}
      />
    </Cluster>
  );
  return (
    <div>
      {isCitybaseAdmin && client.value && (
        <Alert
          height="50px"
          textOverride={cbAdminText}
          showQuitLink={false}
          maxContentWidth="1258px"
          extraStyles={`border: none; border-radius: 0; padding: 0;`}
        />
      )}
      <Navbar className="navbar-top" container expand="lg">
        <NavbarBrand tag={Link} to="/dashboard">
          <HeaderLogo />
        </NavbarBrand>
        <NavbarToggler onClick={toggleUserMenu}>
          <FaUser />
        </NavbarToggler>
        <Collapse isOpen={userMenuCollapsed} navbar>
          <Nav className="flex-grow-1" navbar>
            <NavItem>
              <NavLink tag={Link} to={USER_PROFILE} data-qa="User profile">
                {S.fromMaybe("")(userEmail)}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink href="#" onClick={() => logOut(oktaAuth)}>
                Log Out
              </NavLink>
            </NavItem>
          </Nav>
        </Collapse>
      </Navbar>
      {!hideNavbar && (
        // The beta tab no longer fits one row below xl, so collapse there.
        <Navbar
          className={`navbar-bottom${
            paymentsBetaTabEnabled ? " navbar-bottom--beta" : ""
          }`}
          container
          expand={paymentsBetaTabEnabled ? "xl" : "lg"}
        >
          <NavbarToggler onClick={toggleCommonMenu}>
            <FaBars />
          </NavbarToggler>
          <Collapse isOpen={commonMenuCollapsed} navbar>
            <Nav className="m-auto" navbar>
              <Tab
                slug="/dashboard"
                title="Dashboard"
                compact={paymentsBetaTabEnabled}
              />
              <Tab
                slug="/payments"
                title="Payments"
                compact={paymentsBetaTabEnabled}
              />
              {paymentsBetaTabEnabled && (
                <Tab
                  slug="/payments-beta"
                  title="Payments"
                  badge="Beta"
                  dataQa="Payments - Beta"
                  compact
                />
              )}
              {isCustomerManagementEnabled && (
                <Tab
                  slug="/customers"
                  title="Customers"
                  compact={paymentsBetaTabEnabled}
                />
              )}
              <Tab
                slug="/settings"
                title="Settings"
                compact={paymentsBetaTabEnabled}
              />
              {/*
                The `AdminRoutes` in Routes.js protect this route,
                but the `isCitybaseAdmin` check is also required to prevent the link from rendering.
                This prevents the user from clicking a "broken" link.
              */}
              {isCitybaseAdmin && (
                <Tab
                  containsSubroutes
                  slug="/pos-config/clients/edit"
                  title="Cashiering Configurations"
                  compact={paymentsBetaTabEnabled}
                />
              )}
            </Nav>
          </Collapse>
        </Navbar>
      )}
    </div>
  );
};
export const mapStateToProps = (state) => ({
  header: state.header,
  userEmail: getCurrentUserEmail(state),
  isCustomerManagementEnabled: getCustomerManagementEnabled(state),
  isCitybaseAdmin: currentUserIsCitybaseAdmin(state),
  client: getCurrentUserClient(state),
});

export const mapDispatchToProps = (dispatch) => ({
  toggleCommonMenu: () => dispatch(toggleCommonMenu()),
  toggleUserMenu: () => dispatch(toggleUserMenu()),
  closeMenus: () => dispatch(closeMenus()),
  removeClient: () => dispatch(removeClient()),
  clearClientSettings: () => dispatch(clearClientSettings()),
  clearState: () => dispatch(clearState()),
  clearStateV2Beta: () => dispatch(clearStateV2Beta()),
  resetStateV2Beta: () => dispatch(resetStateV2Beta()),
  clearPeriscopeState: () => dispatch(clearPeriscopeState()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Header);
